
import {
    useEffect,
    useLayoutEffect,
    useState
} from "react";
import {
    ActivityIndicator,
    TouchableOpacity,
    View
} from "react-native";
import type {
    FormInputDataType
} from "./type";
import stylesheet from "./stylesheet";
import {
    FormRegistry
} from "./components/formRegistry";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    NCoreUIKitSnackBar,
    PageContainer,
    Button,
    Dialog,
    Text
} from "ncore-ui-kit";
import {
    useNavigation,
    useRoute,
    type RouteProp
} from "@react-navigation/native";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    FormAnswerType
} from "../../../services/restAPI/actions/eventApplication/types";
import type GuestStackParamList from "../../../types/navigation/guest";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    ChevronLeft,
    Check,
    ClipboardX
} from "lucide-react-native";
import {
    CircleAlert
} from "lucide-react-native/icons";
import validator from "validator";
import {
    RESTService
} from "../../../services/restAPI";
import {
    handleUpload
} from "../../../utils";

const GuestApplyForm = () => {
    const navigation = useNavigation<NativeStackNavigationProp<GuestStackParamList>>();
    const route = useRoute<RouteProp<GuestStackParamList, "GuestApplyForm">>();

    const {
        eventSubID = "",
        eventID = "",
        formID
    } = route.params;

    const {
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [loading,
        setLoading] = useState(true);

    const [formInfo,
        setFormInfo] = useState<{ title?: string; description?: string; }>({});
    const [formInputData,
        setFormInputData] = useState<Array<FormInputDataType>>([]);
    const [exitDialog,
        setExitDialog] = useState(false);

    const inset = useSafeAreaInsets();

    useEffect(() => {
        getForm();
    }, []);

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => <View
                style={{
                    ...stylesheet.headerContainer,
                    paddingTop: inset.top,
                    paddingHorizontal: spaces.spacingMd,
                    backgroundColor: colors.content.container.default
                }}
            >
                <TouchableOpacity
                    style={[
                        stylesheet.backButton,
                        {
                            padding: spaces.spacingSm
                        }
                    ]}
                    onPress={() => {
                        setExitDialog(true);
                    }}
                >
                    <ChevronLeft
                        size={30}
                    />
                </TouchableOpacity>
                <View
                    style={{
                        ...stylesheet.headerContent,
                        top: inset.top + spaces.spacingSm
                    }}
                >
                    <Text
                        variant="titleLargeSize"
                        color="high"
                        numberOfLines={3}
                        style={stylesheet.headerTitle}
                    >
                        {localize("fillForm") || "Formu Doldur"}
                    </Text>
                </View>
            </View>
        });
    }, [navigation]);

    const getForm = () => {
        RESTService.action("GetEventApplicationFormWihoutAuth", {
            formID: formID
        })
            .then((res) => {
                if (res.code === 200 || res.code === 201 || res.code === 202) {
                    const __formInput: Array<FormInputDataType> = res.payload.fields.map(e => {
                        let customType = e.type;
                        if (e.key.startsWith("default_")) {
                            if (e.key === "default_phone") customType = "phone";
                            else if (e.key === "default_mail") customType = "mail";
                            else customType = "string";
                        }
                        return {
                            ...e,
                            type: customType,
                            value: ""
                        };
                    });
                    setFormInfo({
                        title: res.payload.title,
                        description: res.payload.description
                    });
                    setFormInputData(__formInput);
                    setLoading(false);
                } else {
                    setLoading(false);
                    NCoreUIKitSnackBar.open({
                        title: localize("error"),
                        subTitle: res.message || localize("anErrorOccured"),
                        type: "danger",
                        isShowAction: false,
                        autoCloseDelay: 5000,
                        icon: ({
                            color
                        }) => <CircleAlert
                            color={colors.content.icon[color ? color : "default"]}
                        />
                    });
                }
            }).catch((err) => {
                setLoading(false);
                console.log(err);
                NCoreUIKitSnackBar.open({
                    title: localize("error"),
                    subTitle: (err as { message?: string })?.message || localize("anErrorOccured"),
                    type: "danger",
                    isShowAction: false,
                    autoCloseDelay: 5000,
                    icon: ({
                        color
                    }) => <CircleAlert
                        color={colors.content.icon[color ? color : "default"]}
                    />
                });
            });
    };

    const applyForm = async () => {
        let isEmptyValue = false;

        const answers: Array<FormAnswerType> = [];

        let phoneNumber = "";

        formInputData.forEach((e) => {
            const isRequired = e.isRequired !== false;
            const hasValue = e.value && e.value.length > 0;

            if (!isRequired && !hasValue) {
                return;
            }

            if (e.type === "phone") {
                if (hasValue && validator.isMobilePhone(e.value)) {
                    phoneNumber = e.value;
                    answers.push({
                        key: e.key,
                        value: e.value
                    });
                } else {
                    isEmptyValue = true;
                }
            } else if (e.type === "mail") {
                if (hasValue && validator.isEmail(e.value)) {
                    answers.push({
                        key: e.key,
                        value: e.value
                    });
                } else {
                    isEmptyValue = true;
                }
            } else if (e.type === "selectbox" || e.type === "photo") {
                if (hasValue) {
                    answers.push({
                        key: e.key,
                        value: e.value
                    });
                } else {
                    isEmptyValue = true;
                }
            } else {
                const isMinValid = e.minLength ? e.value.length >= e.minLength : true;
                const isMaxValid = e.maxLength ? e.value.length <= e.maxLength : true;

                if (hasValue && isMinValid && isMaxValid) {
                    answers.push({
                        key: e.key,
                        value: e.value
                    });
                } else {
                    isEmptyValue = true;
                }
            }
        });

        if (isEmptyValue) {
            NCoreUIKitSnackBar.open({
                title: localize("error"),
                subTitle: localize("pleaseCheckAllFields"),
                type: "danger",
                isShowAction: false,
                autoCloseDelay: 5000,
                icon: ({
                    color
                }) => <CircleAlert
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
            return;
        }

        setLoading(true);

        try {
            // Fotoğrafların local URI'lerini gizleyerek backend'e atıyoruz.
            const submitAnswers = answers.map(a => {
                const fieldDef = formInputData.find(f => f.key === a.key);
                if (fieldDef && fieldDef.type === "photo") {
                    return {
                        ...a,
                        value: "pending" // Storage'a henüz yüklenmediği için bekliyor olarak işaretliyoruz
                    };
                }
                return a;
            });

            const res = await RESTService.action("EventApplicationWithoutAuth", {
                ...(eventSubID ? {
                    eventSubID
                } : {}),
                ...(eventID ? {
                    eventID
                } : {}),
                phoneNumber: phoneNumber,
                answers: submitAnswers,
                formID: formID
            });

            if (res.code === 200 || res.code === 201 || res.code === 202) {
                const answerID = res.payload?.answerID;

                // Form kaydedildikten sonra (answerID ile) fotoğrafları yüklüyoruz.
                if (answerID) {
                    for (let i = 0; i < answers.length; i++) {
                        const currentAnswer = answers[i];
                        if (!currentAnswer) {
                            continue;
                        }

                        const fieldDef = formInputData.find(f => f.key === currentAnswer.key);

                        if (fieldDef && fieldDef.type === "photo" && currentAnswer.value) {
                            if (currentAnswer.value.startsWith("file://") || currentAnswer.value.startsWith("/") || currentAnswer.value.startsWith("blob:")) {
                                const tokenRes = await RESTService.action("GetUploadTokenForFormPhotoWithoutAuth", {
                                    ...(eventSubID ? {
                                        eventSubID
                                    } : {}),
                                    phoneNumber: phoneNumber || "",
                                    fieldKey: currentAnswer.key,
                                    ...(eventID ? {
                                        eventID
                                    } : {}),
                                    answerID: answerID,
                                    formID: formID
                                });

                                if (tokenRes && tokenRes.payload && tokenRes.payload.uploadToken) {
                                    const uri = currentAnswer.value;
                                    const extensionMatch = uri.match(/\.([a-zA-Z0-9]+)$/);
                                    let extension = "jpg";
                                    let mimeType = "image/jpeg";
                                    if (extensionMatch && extensionMatch[1]) {
                                        extension = extensionMatch[1].toLowerCase();
                                        if (extension === "png") mimeType = "image/png";
                                        else if (extension === "webp") mimeType = "image/webp";
                                        else if (extension === "heic") mimeType = "image/heic";
                                    }

                                    await handleUpload({
                                        uri: uri,
                                        type: mimeType,
                                        name: `photo.${extension}`
                                    }, tokenRes.payload.uploadToken);
                                } else {
                                    console.log("Fotoğraf yükleme token'ı alınamadı.");
                                }
                            }
                        }
                    }
                }

                NCoreUIKitSnackBar.open({
                    title: localize("success"),
                    subTitle: localize("formSubmittedSuccessfully"),
                    type: "success",
                    isShowAction: false,
                    autoCloseDelay: 5000,
                    icon: ({
                        color
                    }) => <Check
                        color={colors.content.icon[color ? color : "default"]}
                    />
                });
                setTimeout(() => {
                    if (navigation.canGoBack()) {
                        navigation.goBack();
                    } else {
                        navigation.getParent()?.reset({
                            index: 0,
                            routes: [
                                {
                                    name: "Guest"
                                }
                            ]
                        });
                    }
                }, 1500);
            } else {
                let errorMessage = res.message || "anErrorOccured";
                if(res.message === "user-banned") {
                    errorMessage = localize("bannedUserFormError");
                } else if(res.message === "active-member-must-login") {
                    errorMessage = localize("activeMemberMustLoginFormError");
                } else if(res.message === "phone-number-already-used") {
                    errorMessage = localize("phoneNumberAlreadyUsed");
                } else if(res.message === "form-not-active" || res.message === "form-is-not-active") {
                    errorMessage = localize("formIsNotActive");
                } else {
                    errorMessage = localize(res.message as never) || localize("anErrorOccured");
                }
                throw new Error(errorMessage);
            }
        } catch (err) {
            console.log(err);
            NCoreUIKitSnackBar.open({
                title: localize("error"),
                subTitle: (err as { message?: string })?.message || localize("anErrorOccured"),
                type: "danger",
                isShowAction: false,
                autoCloseDelay: 5000,
                icon: ({
                    color
                }) => <CircleAlert
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (fieldKey: string, value: string) => {
        const _formInputData: Array<FormInputDataType> = JSON.parse(JSON.stringify(formInputData));

        const _index = _formInputData.findIndex(e => e.key === fieldKey);
        if (_index !== -1 && _formInputData[_index]) {
            _formInputData[_index].value = value;

            setFormInputData(_formInputData);
        }
    };

    const isFormValid = formInputData.length > 0 && formInputData.every((e) => {
        const isRequired = e.isRequired !== false;
        const hasValue = e.value && e.value.length > 0;

        if (!isRequired && !hasValue) {
            return true;
        }

        if (e.type === "phone") {
            return hasValue && validator.isMobilePhone(e.value);
        }
        if (e.type === "mail") {
            return hasValue && validator.isEmail(e.value);
        }
        if (e.type === "selectbox" || e.type === "photo") {
            return hasValue;
        }
        return hasValue && e.value.length <= e.maxLength && e.value.length >= e.minLength;
    });

    if (loading) {
        return <PageContainer
            style={stylesheet.loadingContainer}
        >
            <ActivityIndicator
                size={40}
                color={colors.content.icon.default}
            />
        </PageContainer>;
    }

    return <PageContainer
        isScrollable={true}
        backgroundColor="default"
        scrollViewProps={{
            showsVerticalScrollIndicator: false,
            contentContainerStyle: {
                paddingTop: spaces.spacingXl
            }
        }}
    >
        <View
            style={{
                marginBottom: spaces.spacingXl
            }}
        >
            {formInfo.title ? <Text
                variant="titleLargeSize"
                color="high"
                style={{
                    marginBottom: spaces.spacingXs
                }}
            >
                {formInfo.title}
            </Text> : null}
            {formInfo.description ? <Text
                variant="bodyMediumSize"
                color="mid"
                style={{
                    marginBottom: spaces.spacingXl
                }}
            >
                {formInfo.description}
            </Text> : null}

            {formInputData.map((field) => {
                const WidgetComponent = FormRegistry[field.type];

                if (!WidgetComponent) {
                    console.warn(`Bilinmeyen component tipi: ${field.type}`);
                    return null;
                }

                return <WidgetComponent
                    key={field.key}
                    field={field}
                    value={formInputData.find(e => e.key === field.key)?.value || ""}
                    onChange={(val: string) => handleInputChange(field.key, val)}
                    eventID={eventID}
                    eventSubID={eventSubID}
                    formID={formID}
                    phoneNumber={formInputData.find(e => e.type === "phone" || e.key === "default_phone")?.value || ""}
                />;
            })}
        </View>
        <Button
            title={localize("submitApplication")}
            spreadBehaviour="stretch"
            customColor="brandPrimary"
            customBorderColor="brandPrimary"
            size="large"
            isDisabled={!isFormValid}
            style={{
                marginBottom: spaces.spacingXl
            }}
            onPress={() => {
                if (!isFormValid) {
                    NCoreUIKitSnackBar.open({
                        title: localize("error"),
                        subTitle: localize("pleaseCheckAllFields"),
                        type: "danger",
                        isShowAction: false,
                        autoCloseDelay: 5000,
                        icon: ({
                            color
                        }) => <CircleAlert
                            color={colors.content.icon[color ? color : "default"]}
                        />
                    });
                    return;
                }
                applyForm();
            }}
        />
        <Dialog
            headerComponent={<View
                style={stylesheet.dialogHeaderContainer}
            >
                <View
                    style={[
                        stylesheet.dialogIconWrapper,
                        {
                            marginBottom: spaces.spacingLg
                        }
                    ]}
                >
                    <ClipboardX
                        size={40}
                        color={colors.content.icon.warning}
                    />
                </View>
                <Text
                    variant="titleLargeSize"
                    color="high"
                    style={stylesheet.dialogTitle}
                >
                    {localize("areYouSureToExit")}
                </Text>
            </View>}
            isVisible={exitDialog}
            onOverlayPress={() => setExitDialog(false)}
            primaryButtonProps={{
                onPress: () => {
                    setExitDialog(false);
                    if (navigation.canGoBack()) {
                        navigation.goBack();
                    } else {
                        navigation.getParent()?.reset({
                            index: 0,
                            routes: [
                                {
                                    name: "Guest"
                                }
                            ]
                        });
                    }
                },
                title: localize("yes")
            }}
            secondaryButtonProps={{
                onPress: () => setExitDialog(false),
                title: localize("no")
            }}
            variant="yes-no"
        />
    </PageContainer>;
};
export default GuestApplyForm;
